import { SpoolDto, SpoolStatus } from 'providers/api';
import PCNode from '../Nodes/PCNode';
import { isSyntheticNodeId } from '../Nodes';
import PCPipe from '../Pipes/PCPipe';
import PCSpool from './PCSpool';

export const isBorehole = (a: PCNode, b: PCNode): boolean => {
  const dx = b.coords.x - a.coords.x;
  const dy = b.coords.y - a.coords.y;
  const dz = Math.abs(b.coords.z - a.coords.z);
  const horizontal = Math.hypot(dx, dy);
  if (horizontal === 0) return true; // purely vertical
  const angleFromHorizontal = Math.atan2(dz, horizontal); // radians
  return angleFromHorizontal > Math.PI / 4; // > 45°
};

// Only ever applied when a spool is first created. Once persisted, the spool's own flags are the
// source of truth and are never re-derived from geometry on load.
export const getDefaultFlowFlags = (a: PCNode, b: PCNode, biDirectionalBoreholes?: boolean): { allowAB: boolean; allowBA: boolean } => {
  const borehole = isBorehole(a, b);
  if (!borehole || biDirectionalBoreholes) return { allowAB: true, allowBA: true };
  if (a.coords.z > b.coords.z) return { allowAB: true, allowBA: false }; // A above B
  if (b.coords.z > a.coords.z) return { allowAB: false, allowBA: true }; // B above A
  // Equal heights treated as non-directional in borehole context -> allow both
  return { allowAB: true, allowBA: true };
};

// Bi-directional boreholes route every borehole both ways, overriding its stored flags without touching them.
export const routableFlow = (spool: PCSpool, biDirectionalBoreholes: boolean): { allowAB: boolean; allowBA: boolean } => (
  biDirectionalBoreholes && isBorehole(spool.pointA, spool.pointB)
    ? { allowAB: true, allowBA: true }
    : { allowAB: spool.allowFlowAB, allowBA: spool.allowFlowBA }
);

// Anything other than Inactive routes, matching the server.
export const isSpoolActive = (spool: PCSpool): boolean => spool.status !== SpoolStatus.Inactive;

export const isSpoolShown = (spool: PCSpool, showInactive: boolean): boolean => showInactive || isSpoolActive(spool);

export const DISCHARGE_SPOOL_PREFIX = 'dischargeSpool-';

// The route builder spools up presentation-only edges - the stope's discharge pipe arrays and the
// zero-length booster/choke twins - that are never persisted and therefore never carry throughput or
// an inspection point. Anything that sends a route's spools to the server steps over them.
export const isSyntheticSpool = (spool: PCSpool): boolean => spool.spoolId.startsWith(DISCHARGE_SPOOL_PREFIX)
  || isSyntheticNodeId(spool.pointA.id)
  || isSyntheticNodeId(spool.pointB.id);

// How a spool is named to a person: its two ends and its pipe. Spool ids are opaque GUIDs.
export const describeSpool = (spool: PCSpool): string => {
  const pipe = spool.pipe?.name || spool.pipe?.pipeId;
  return `${spool.pointA.nodeName} -> ${spool.pointB.nodeName}${pipe ? ` (${pipe})` : ''}`;
};

// The name an unnamed spool goes by. Derived rather than stored, so it follows its nodes when they are renamed.
export const spoolEndsLabel = (spool: PCSpool): string => `${spool.pointA?.nodeName || spool.pointA?.id || ''} → ${spool.pointB?.nodeName || spool.pointB?.id || ''}`;

export const spoolDisplayName = (spool: PCSpool): string => spool.name?.trim() || spoolEndsLabel(spool);

/**
 * Flow-loop metres attributed to each node, keyed by node id.
 *
 * A flow loop is a length of pipe coiled into an edge, so it belongs to the spool. BOTH endpoints of a choked
 * spool count: deleting either one prunes the spool and takes the loop with it, so both have to be protected
 * from deletion — and the loop marker has to appear on every node the delete guard protects, or the
 * protection reads as arbitrary. One rule, one helper, used by the guard and the overlay alike.
 */
export const flowLoopMetresByNodeId = (spools: PCSpool[]): Record<string, number> => spools
  .filter((spool) => spool.chokeLength > 0)
  .reduce<Record<string, number>>((acc, spool) => ({
    ...acc,
    [spool.pointA.id]: (acc[spool.pointA.id] ?? 0) + spool.chokeLength,
    [spool.pointB.id]: (acc[spool.pointB.id] ?? 0) + spool.chokeLength,
  }), {});

export interface SpoolLengthTotals {
  pipeMetres: number;
  flowLoopMetres: number;
}

// spool.length() is pipe plus flow loop; the split is kept so a selection can report both.
export const sumSpoolLengths = (spools: PCSpool[]): SpoolLengthTotals => spools.reduce<SpoolLengthTotals>(
  (acc, spool) => ({
    pipeMetres: acc.pipeMetres + spool.pointA.coords.length3dTo(spool.pointB.coords),
    flowLoopMetres: acc.flowLoopMetres + (spool.chokeLength || 0),
  }),
  { pipeMetres: 0, flowLoopMetres: 0 },
);

export const loadSpools = (
  nodes: Record<string, PCNode>,
  pipes: Record<string, PCPipe>,
  spools: SpoolDto[],
): Record<string, PCSpool> => (spools || []).reduce<Record<string, PCSpool>>((acc, spool) => {
  const pointA = nodes[spool.nodeAId];
  const pointB = nodes[spool.nodeBId];
  if (!pointA || !pointB) return acc;

  // Booster/choke twins are presentation-only and are spooled up by the route builder, never persisted.
  if (isSyntheticNodeId(spool.nodeAId) || isSyntheticNodeId(spool.nodeBId)) return acc;

  // A spool pointing at a deleted pipe type must not vanish from the model — that reads as a whole
  // network disappearing on cold load. Fall back to the endpoints' own pipes before giving up.
  const pipe = pipes[spool.pipeId] ?? pipes[pointA.pipeId] ?? pipes[pointB.pipeId];
  if (!pipe) {
    // eslint-disable-next-line no-console
    console.error(`Spool ${spool.spoolId} (${spool.nodeAId} - ${spool.nodeBId}): no pipe resolved for pipeId ${spool.pipeId}`);
    return acc;
  }

  acc[spool.spoolId] = new PCSpool(
    spool.spoolId,
    spool.chokeLength,
    pointA,
    pointB,
    pipe,
    spool.allowFlowAB,
    spool.allowFlowBA,
    {
      totalVolumeThroughSpool: spool.totalVolumeThroughSpool ?? 0,
      totalDryTonnesThroughSpool: spool.totalDryTonnesThroughSpool ?? 0,
      name: spool.name,
      installedOn: spool.installedOn,
      removedOn: spool.removedOn,
      status: spool.status,
      other: spool.other,
      level: spool.level,
      area: spool.area,
    },
  );
  return acc;
}, {});
